import type { Backend } from '@alterrego/core-config';
import type { AlterregoUser } from './types';

/**
 * Client HTTP pour le backend Alterrego.
 * Gère l'échange de tokens provider → JWT Alterrego.
 */
export class AuthClient {
  private baseUrl: string;

  constructor(backend: Backend) {
    this.baseUrl = backend.url.replace(/\/$/, '');
  }

  /**
   * Échange un authorization code Apple contre un JWT Alterrego.
   */
  async exchangeAppleCode(params: {
    code: string;
    idToken: string;
    bundleId: string;
    env: string;
  }): Promise<{ jwt: string; refreshToken: string; user: AlterregoUser }> {
    const res = await fetch(`${this.baseUrl}/auth/apple/exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Alterrego-Env': params.env,
      },
      body: JSON.stringify({
        code: params.code,
        idToken: params.idToken,
        bundleId: params.bundleId,
      }),
    });

    if (!res.ok) {
      const body = await res.text();
      throw new Error(`Apple auth exchange failed (${res.status}): ${body}`);
    }

    return res.json();
  }

  /**
   * Rafraîchit un JWT Alterrego expiré via le refresh token.
   */
  async refreshJwt(refreshToken: string, env: string): Promise<{ jwt: string; refreshToken: string }> {
    const res = await fetch(`${this.baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Alterrego-Env': env,
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      throw new Error(`Token refresh failed (${res.status})`);
    }

    return res.json();
  }

  /**
   * Supprime le compte utilisateur (cascade).
   */
  async deleteAccount(jwt: string, env: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/users/me/delete`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Alterrego-Env': env,
      },
    });

    if (!res.ok) {
      throw new Error(`Account deletion failed (${res.status})`);
    }
  }

  /**
   * Récupère le profil utilisateur.
   */
  async getProfile(jwt: string, env: string): Promise<AlterregoUser> {
    const res = await fetch(`${this.baseUrl}/users/me`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Alterrego-Env': env,
      },
    });

    if (!res.ok) {
      throw new Error(`Get profile failed (${res.status})`);
    }

    return res.json();
  }
}
